/**
 * Financial Module API Client
 *
 * Sprint 6A - Business Analytics
 * Wraps REST API endpoints from Stage 5
 */

import type {
  EntityFinancialRecord,
  CreateEntityFinancialRecordInput,
  FinancialPeriod,
  CreateFinancialPeriodInput,
  ProfitLossLineItem,
  UpdatePLLineItemInput,
  BalanceSheetLineItem,
  UpdateBSLineItemInput,
  CashFlowLineItem,
  UpdateCFLineItemInput,
  NormalisationAdjustment,
  CreateNormalisationInput,
  NormalisationTemplate,
  CalculatedRatio,
  HealthScore,
  BalanceSheetValidation,
  EbitdaSummary,
  CustomLineItem,
  CustomLineItemValue,
  CreateCustomLineItemInput,
  UpdateCustomLineItemInput,
  CustomLineItemStatementType,
  FinancialCascadeResponse,
  FinancialCascadeView,
} from '@/types/financial';
import { getAuthHeaders } from '@/lib/queryClient';
import { financialWriteError } from '../../../../shared/financial-write-errors';

const API_BASE = '/api/financial';

async function throwFinancialWriteError(res: Response, fallback: string): Promise<never> {
  const body = await res.json().catch(() => null);
  const safe = financialWriteError(res.status, body?.code);
  throw Object.assign(new Error(safe?.error ?? fallback), { status: res.status, code: safe?.code });
}

// ============================================================================
// Authentication Helper
// ============================================================================

// Reference to native fetch (avoids bare fetch detection in authFetch implementation)
const nativeauthFetch = globalThis.fetch.bind(globalThis);

/**
 * Wrapper around fetch that automatically injects auth token.
 * Uses shared getAuthHeaders() from queryClient (Clerk or no-auth).
 */
async function authFetch(url: string, options?: RequestInit): Promise<Response> {
  const authHeaders = await getAuthHeaders();
  return nativeauthFetch(url, {
    ...options,
    headers: {
      ...authHeaders,
      ...options?.headers,
    },
  });
}

// ============================================================================
// Data Normalization Helpers
// ============================================================================

/**
 * Normalize financial line items by converting string amounts to numbers.
 * PostgreSQL DECIMAL fields return as strings to preserve precision.
 * UI components expect numbers for formatting (toLocaleString, etc.).
 */
function normalizeAmount(value: string | number): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return parseFloat(value) || 0;
  return 0;
}

function normalizePLItems(items: any[]): ProfitLossLineItem[] {
  return items.map(item => ({
    ...item,
    // Keep the stored decimal for strict calculations without changing display consumers.
    storedAmount: item.amount,
    amount: normalizeAmount(item.amount),
  }));
}

function normalizeBSItems(items: any[]): BalanceSheetLineItem[] {
  return items.map(item => ({
    ...item,
    amount: normalizeAmount(item.amount),
  }));
}

function normalizeCFItems(items: any[]): CashFlowLineItem[] {
  return items.map(item => ({
    ...item,
    amount: normalizeAmount(item.amount),
  }));
}

function normalizeNormalisations(items: any[]): NormalisationAdjustment[] {
  return items.map(item => ({
    ...item,
    amount: normalizeAmount(item.amount),
  }));
}

async function throwFinancialApiError(res: Response, fallback: string): Promise<never> {
  const errorData = await res.json().catch(() => ({ error: res.statusText }));
  const error: any = new Error(errorData.error || fallback);
  error.code = errorData.code;
  error.status = res.status;
  throw error;
}

// ============================================================================
// Entity Financial Record
// ============================================================================

export async function getEntityRecord(accountId: string): Promise<EntityFinancialRecord | null> {
  const res = await authFetch(`${API_BASE}/entity-records?account_id=${accountId}`);
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error(`Failed to fetch entity record: ${res.statusText}`);
  }
  const data = await res.json();
  return data.data?.[0] ?? null;
}

export async function createEntityRecord(
  input: CreateEntityFinancialRecordInput
): Promise<EntityFinancialRecord> {
  const res = await authFetch(`${API_BASE}/entity-records`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    // Parse error response to get error code and message
    const errorData = await res.json().catch(() => ({ error: res.statusText }));
    const error: any = new Error(errorData.error || `Failed to create entity record: ${res.statusText}`);
    error.code = errorData.code; // Preserve error code from backend (e.g., DUPLICATE_FINANCIAL_RECORD)
    error.status = res.status;
    throw error;
  }
  const data = await res.json();
  // API returns record directly (not wrapped in { data }), handle both formats
  return data.data ?? data;
}

export async function createFinancialYear(input: {
  entityRecord: CreateEntityFinancialRecordInput;
  period: Omit<CreateFinancialPeriodInput, 'entityFinancialRecordId'>;
}): Promise<{ entityRecord: EntityFinancialRecord; period: FinancialPeriod }> {
  const res = await authFetch(`${API_BASE}/years`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    return throwFinancialApiError(res, 'Failed to create financial year');
  }
  return res.json();
}

export async function updateEntityRecord(
  id: string,
  input: Partial<CreateEntityFinancialRecordInput>
): Promise<EntityFinancialRecord> {
  const res = await authFetch(`${API_BASE}/entity-records/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to update entity record: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

// ============================================================================
// Financial Periods
// ============================================================================

export async function getPeriods(entityRecordId: string): Promise<FinancialPeriod[]> {
  const res = await authFetch(`${API_BASE}/periods?entity_record_id=${entityRecordId}`);
  if (!res.ok) throw new Error(`Failed to fetch periods: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? [];
}

export async function createPeriod(input: CreateFinancialPeriodInput): Promise<FinancialPeriod> {
  const res = await authFetch(`${API_BASE}/periods`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to create period: ${res.statusText}`);
  const data = await res.json();
  // API returns period directly (not wrapped in { data }), handle both formats
  return data.data ?? data;
}

export async function lockPeriod(periodId: string): Promise<{ period: FinancialPeriod; ratiosCalculated: boolean }> {
  const res = await authFetch(`${API_BASE}/periods/${periodId}/lock`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) await throwFinancialWriteError(res, 'The period could not be locked.');
  const data = await res.json();
  return { period: data.period, ratiosCalculated: data.ratiosCalculated === true };
}

export async function deletePeriod(periodId: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/periods/${periodId}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to delete period: ${res.statusText}`);
}

// ============================================================================
// Profit & Loss
// ============================================================================

export async function getProfitLoss(periodId: string): Promise<ProfitLossLineItem[]> {
  const res = await authFetch(`${API_BASE}/profit-loss?period_id=${periodId}`);
  // Return empty array for 404 (no data exists yet — matches getBalanceSheet pattern)
  if (res.status === 404) return [];
  if (!res.ok) throw new Error(`Failed to fetch P&L: ${res.statusText}`);
  const data = await res.json();
  return normalizePLItems(data.data ?? []);
}

export async function getFinancialCascade(
  entityId: string,
  options: { periodId?: string; view?: FinancialCascadeView } = {},
): Promise<FinancialCascadeResponse> {
  const params = new URLSearchParams();
  if (options.periodId) params.set('period', options.periodId);
  if (options.view) params.set('view', options.view);
  const query = params.toString();
  const res = await authFetch(`/api/financial/entities/${entityId}/cascade${query ? `?${query}` : ''}`);
  if (!res.ok) throw new Error(`Failed to fetch cascade: ${res.statusText}`);
  return res.json();
}

export async function createPLLineItem(input: {
  financialPeriodId: string;
  lineItem: string;
  amount: number;
  manualAdjustmentReasonCode?: string;
  manualAdjustmentReasonText?: string;
}): Promise<ProfitLossLineItem> {
  // Amount must be string for Drizzle numeric column
  const payload = { ...input, amount: String(input.amount) };
  const res = await authFetch(`${API_BASE}/profit-loss`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to create P&L line item: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? data;
}

export async function updatePLLineItem(
  id: string,
  input: UpdatePLLineItemInput
): Promise<ProfitLossLineItem> {
  const { expectedUpdatedAt, ...inputFields } = input;
  // Amount must be string for Drizzle numeric column
  const payload = inputFields.amount !== undefined
    ? { ...inputFields, amount: String(inputFields.amount), expectedUpdatedAt }
    : { ...inputFields, expectedUpdatedAt };
  const res = await authFetch(`${API_BASE}/profit-loss/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await throwFinancialApiError(res, `Failed to update P&L line item: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

export async function recalculatePL(periodId: string): Promise<ProfitLossLineItem[]> {
  const res = await authFetch(`${API_BASE}/profit-loss/recalculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period_id: periodId }),
  });
  if (!res.ok) throw new Error(`Failed to recalculate P&L: ${res.statusText}`);
  const data = await res.json();
  return normalizePLItems(data.data ?? []);
}

// ============================================================================
// Balance Sheet
// ============================================================================

export async function getBalanceSheet(periodId: string): Promise<BalanceSheetLineItem[]> {
  const res = await authFetch(`${API_BASE}/balance-sheet?period_id=${periodId}`);
  // Return empty array for 404 (no data exists yet)
  if (res.status === 404) {
    return [];
  }
  if (!res.ok) throw new Error(`Failed to fetch Balance Sheet: ${res.statusText}`);
  const data = await res.json();
  return normalizeBSItems(data.data ?? []);
}

export async function createBSLineItem(input: {
  financialPeriodId: string;
  lineItem: string;
  amount: number;
  manualAdjustmentReasonCode?: string;
  manualAdjustmentReasonText?: string;
}): Promise<BalanceSheetLineItem> {
  const payload = { ...input, amount: String(input.amount) };
  const res = await authFetch(`${API_BASE}/balance-sheet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await throwFinancialApiError(res, `Failed to create BS line item: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? data;
}

export async function updateBSLineItem(
  id: string,
  input: UpdateBSLineItemInput
): Promise<BalanceSheetLineItem> {
  const { expectedUpdatedAt, ...inputFields } = input;
  // Amount must be string for Drizzle numeric column
  const payload = inputFields.amount !== undefined
    ? { ...inputFields, amount: String(inputFields.amount), expectedUpdatedAt }
    : { ...inputFields, expectedUpdatedAt };
  const res = await authFetch(`${API_BASE}/balance-sheet/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) await throwFinancialApiError(res, `Failed to update BS line item: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

export async function validateBalanceSheet(periodId: string): Promise<BalanceSheetValidation> {
  const res = await authFetch(`${API_BASE}/validate-balance-sheet`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ financialPeriodId: periodId }),
  });
  if (!res.ok) throw new Error(`Failed to validate Balance Sheet: ${res.statusText}`);
  const data = await res.json();
  return {
    isBalanced: data.isValid,
    totalAssets: data.assets,
    totalLiabilities: data.liabilities,
    totalEquity: data.equity,
    discrepancy: data.difference,
  };
}

// ============================================================================
// Cash Flow
// ============================================================================

export async function getCashFlow(periodId: string): Promise<CashFlowLineItem[]> {
  const res = await authFetch(`${API_BASE}/cashflow?period_id=${periodId}`);
  if (!res.ok) throw new Error(`Failed to fetch Cash Flow: ${res.statusText}`);
  const data = await res.json();
  return normalizeCFItems(data.data ?? []);
}

export async function updateCFLineItem(
  id: string,
  input: UpdateCFLineItemInput
): Promise<CashFlowLineItem> {
  const res = await authFetch(`${API_BASE}/cashflow/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to update CF line item: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

export async function clearCFOverride(id: string): Promise<CashFlowLineItem> {
  const res = await authFetch(`${API_BASE}/cashflow/${id}/clear-override`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!res.ok) throw new Error(`Failed to clear CF override: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

export async function regenerateCashFlow(periodId: string): Promise<CashFlowLineItem[]> {
  const res = await authFetch(`${API_BASE}/cashflow/regenerate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period_id: periodId }),
  });
  if (!res.ok) throw new Error(`Failed to regenerate Cash Flow: ${res.statusText}`);
  const data = await res.json();
  return normalizeCFItems(data.data ?? []);
}

// ============================================================================
// EBITDA Normalization
// ============================================================================

export async function getNormalisations(periodId: string): Promise<NormalisationAdjustment[]> {
  const res = await authFetch(`${API_BASE}/normalisation?period_id=${periodId}`);
  if (!res.ok) throw new Error(`Failed to fetch normalisations: ${res.statusText}`);
  const data = await res.json();
  return normalizeNormalisations(data.data ?? []);
}

export async function createNormalisation(
  input: CreateNormalisationInput
): Promise<NormalisationAdjustment> {
  const res = await authFetch(`${API_BASE}/normalisation`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => null);
    throw new Error(typeof body?.error === 'string' ? body.error : `Failed to create normalisation: ${res.statusText}`);
  }
  const data = await res.json();
  // API returns adjustment directly, not wrapped
  return data.data ?? data;
}

export async function updateNormalisation(
  id: string,
  input: Partial<CreateNormalisationInput>
): Promise<NormalisationAdjustment> {
  const res = await authFetch(`${API_BASE}/normalisation/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to update normalisation: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

export async function deleteNormalisation(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/normalisation/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to delete normalisation: ${res.statusText}`);
}

export async function getNormalisationTemplates(): Promise<NormalisationTemplate[]> {
  const res = await authFetch(`${API_BASE}/normalisation/templates`);
  if (!res.ok) throw new Error(`Failed to fetch normalisation templates: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? [];
}

export async function calculateEbitdaSummary(periodId: string): Promise<EbitdaSummary> {
  const res = await authFetch(`${API_BASE}/normalisation/ebitda-summary?period_id=${periodId}`);
  if (!res.ok) throw new Error(`Failed to calculate EBITDA summary: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

// ============================================================================
// Financial Ratios
// ============================================================================

export async function getRatios(periodId: string): Promise<CalculatedRatio[]> {
  const res = await authFetch(`${API_BASE}/ratios?period_id=${periodId}`);
  if (!res.ok) throw new Error(`Failed to fetch ratios: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? [];
}

export async function calculateRatios(periodId: string): Promise<CalculatedRatio[]> {
  const res = await authFetch(`${API_BASE}/ratios/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period_id: periodId }),
  });
  if (!res.ok) await throwFinancialWriteError(res, 'Ratios could not be calculated.');
  const data = await res.json();
  return data.data ?? [];
}

// ============================================================================
// Health Score
// ============================================================================

export async function getHealthScore(periodId: string): Promise<HealthScore | null> {
  const res = await authFetch(`${API_BASE}/health-score?period_id=${periodId}`);
  if (!res.ok) {
    if (res.status === 404) return null;
    throw new Error(`Failed to fetch health score: ${res.statusText}`);
  }
  const data = await res.json();
  return data.data ?? null;
}

/**
 * Get health scores by entity record ID.
 * Returns latest score and history for the Health Score Panel.
 */
export async function getHealthScores(recordId: string): Promise<{
  latestScore: HealthScore | null;
  history: { assessmentDate: string; overallScore: number | null; healthRating: string | null }[];
}> {
  const res = await authFetch(`${API_BASE}/health-scores?recordId=${recordId}`);
  if (!res.ok) {
    if (res.status === 404) return { latestScore: null, history: [] };
    throw new Error(`Failed to fetch health scores: ${res.statusText}`);
  }
  const data = await res.json();
  return data;
}

export async function calculateHealthScore(periodId: string): Promise<HealthScore> {
  const res = await authFetch(`${API_BASE}/health-score/calculate`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ period_id: periodId }),
  });
  if (!res.ok) throw new Error(`Failed to calculate health score: ${res.statusText}`);
  const data = await res.json();
  return data.data;
}

/**
 * Calculate health score by entity record ID.
 * Used by Health Score Panel recalculate button.
 */
export async function calculateHealthScoreByRecord(entityFinancialRecordId: string, basedOnPeriodId?: string): Promise<HealthScore> {
  const res = await authFetch(`${API_BASE}/calculate-health-score`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ entityFinancialRecordId, basedOnPeriodId }),
  });
  if (!res.ok) throw new Error(`Failed to calculate health score: ${res.statusText}`);
  return await res.json();
}

// ============================================================================
// Custom Line Items (Sprint 11)
// ============================================================================

function normalizeCustomLineItemValues(values: any[]): CustomLineItemValue[] {
  return values.map(value => ({
    ...value,
    amount: normalizeAmount(value.amount),
  }));
}

export async function getCustomLineItems(
  entityRecordId: string,
  statementType: CustomLineItemStatementType
): Promise<{ definitions: CustomLineItem[]; values: CustomLineItemValue[] }> {
  const res = await authFetch(
    `${API_BASE}/custom-line-items?entity_record_id=${entityRecordId}&statement_type=${statementType}`
  );
  if (!res.ok) throw new Error(`Failed to fetch custom line items: ${res.statusText}`);
  const data = await res.json();
  return {
    definitions: data.data?.definitions ?? data.definitions ?? [],
    values: normalizeCustomLineItemValues(data.data?.values ?? data.values ?? []),
  };
}

export async function createCustomLineItem(
  input: CreateCustomLineItemInput
): Promise<CustomLineItem> {
  const res = await authFetch(`${API_BASE}/custom-line-items`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to create custom line item: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? data;
}

export async function updateCustomLineItem(
  id: string,
  input: UpdateCustomLineItemInput
): Promise<CustomLineItem> {
  const res = await authFetch(`${API_BASE}/custom-line-items/${id}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Failed to update custom line item: ${res.statusText}`);
  const data = await res.json();
  return data.data ?? data;
}

export async function deleteCustomLineItem(id: string): Promise<void> {
  const res = await authFetch(`${API_BASE}/custom-line-items/${id}`, {
    method: 'DELETE',
  });
  if (!res.ok) throw new Error(`Failed to delete custom line item: ${res.statusText}`);
}

export async function upsertCustomLineItemValue(input: {
  customLineItemId: string;
  financialPeriodId: string;
  amount: number;
  notes?: string;
}): Promise<CustomLineItemValue> {
  // Amount must be string for Drizzle numeric column
  const payload = { ...input, amount: String(input.amount) };
  const res = await authFetch(`${API_BASE}/custom-line-item-values`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to upsert custom line item value: ${res.statusText}`);
  const data = await res.json();
  const value = data.data ?? data;
  return {
    ...value,
    amount: normalizeAmount(value.amount),
  };
}
